import type { JSONContent } from '@tiptap/react';

/**
 * Convert Tiptap JSON to Markdown string.
 * This handles the block types used in Stele's editor.
 */
export function tiptapToMarkdown(doc: JSONContent): string {
  if (!doc.content) return '';
  return doc.content.map(node => blockToMarkdown(node)).join('\n\n');
}

function blockToMarkdown(node: JSONContent): string {
  switch (node.type) {
    case 'heading': {
      const level = (node.attrs?.level as number) ?? 1;
      const prefix = '#'.repeat(level);
      return `${prefix} ${inlineToMarkdown(node.content)}`;
    }
    case 'paragraph':
      return inlineToMarkdown(node.content);
    case 'bulletList':
      return listToMarkdown(node.content, 'bullet', 0);
    case 'orderedList':
      return listToMarkdown(node.content, 'ordered', 0);
    case 'taskList':
      return listToMarkdown(node.content, 'task', 0);
    case 'codeBlock': {
      const lang = (node.attrs?.language as string) ?? '';
      const code = node.content?.map(c => c.text ?? '').join('') ?? '';
      return `\`\`\`${lang}\n${code}\n\`\`\``;
    }
    case 'blockquote': {
      const inner = node.content?.map(n => blockToMarkdown(n)).join('\n\n') ?? '';
      return inner.split('\n').map(line => `> ${line}`).join('\n');
    }
    case 'horizontalRule':
      return '---';
    case 'image': {
      const src = (node.attrs?.src as string) ?? '';
      const alt = (node.attrs?.alt as string) ?? '';
      const title = (node.attrs?.title as string) ?? '';
      if (title) return `![${alt}](${src} "${title}")`;
      return `![${alt}](${src})`;
    }
    case 'callout': {
      const calloutType = (node.attrs?.type as string) ?? 'info';
      const inner = node.content?.map(n => blockToMarkdown(n)).join('\n\n') ?? '';
      return `> [!${calloutType.toUpperCase()}]\n${inner.split('\n').map(line => `> ${line}`).join('\n')}`;
    }
    default:
      return inlineToMarkdown(node.content);
  }
}

function listToMarkdown(items: JSONContent[] | undefined, type: 'bullet' | 'ordered' | 'task', indent: number): string {
  if (!items) return '';
  const lines: string[] = [];
  let counter = 1;

  for (const item of items) {
    const prefix = ' '.repeat(indent * 2);
    const content = item.content ?? [];
    const textParts: string[] = [];
    const subLists: string[] = [];

    for (const child of content) {
      if (child.type === 'bulletList') {
        subLists.push(listToMarkdown(child.content, 'bullet', indent + 1));
      } else if (child.type === 'orderedList') {
        subLists.push(listToMarkdown(child.content, 'ordered', indent + 1));
      } else if (child.type === 'taskList') {
        subLists.push(listToMarkdown(child.content, 'task', indent + 1));
      } else {
        textParts.push(inlineToMarkdown(child.content));
      }
    }

    const text = textParts.join(' ');

    if (type === 'task') {
      const checked = item.attrs?.checked ? 'x' : ' ';
      lines.push(`${prefix}- [${checked}] ${text}`);
    } else if (type === 'ordered') {
      lines.push(`${prefix}${counter}. ${text}`);
      counter++;
    } else {
      lines.push(`${prefix}- ${text}`);
    }

    for (const sub of subLists) {
      lines.push(sub);
    }
  }

  return lines.join('\n');
}

function inlineToMarkdown(content: JSONContent[] | undefined): string {
  if (!content) return '';
  return content.map(node => {
    let text = node.text ?? '';

    if (node.type === 'hardBreak') return '\n';

    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'bold':
            text = `**${text}**`;
            break;
          case 'italic':
            text = `*${text}*`;
            break;
          case 'code':
            text = `\`${text}\``;
            break;
          case 'strike':
            text = `~~${text}~~`;
            break;
          case 'link': {
            const href = (mark.attrs?.href as string) ?? '';
            text = `[${text}](${href})`;
            break;
          }
        }
      }
    }

    return text;
  }).join('');
}

/**
 * Convert Markdown string to Tiptap JSON document.
 * This is a simplified parser that handles the blocks Stele uses.
 */
export function markdownToTiptap(markdown: string): JSONContent {
  const lines = markdown.split('\n');
  const content: JSONContent[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip empty lines
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      content.push({ type: 'horizontalRule' });
      i++;
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      content.push({
        type: 'heading',
        attrs: { level: Math.min(level, 3) },
        content: parseInline(headingMatch[2]),
      });
      i++;
      continue;
    }

    // Code block
    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      content.push({
        type: 'codeBlock',
        attrs: { language: lang || null },
        content: codeLines.length > 0 ? [{ type: 'text', text: codeLines.join('\n') }] : undefined,
      });
      continue;
    }

    // Callout (GitHub-style: > [!TYPE])
    const calloutMatch = line.match(/^>\s*\[!(\w+)\]/);
    if (calloutMatch) {
      const calloutType = calloutMatch[1].toLowerCase();
      const calloutLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].startsWith('>')) {
        calloutLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      const innerMd = calloutLines.join('\n');
      const innerDoc = markdownToTiptap(innerMd);
      content.push({
        type: 'callout',
        attrs: { type: calloutType },
        content: innerDoc.content ?? [{ type: 'paragraph' }],
      });
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      const innerMd = quoteLines.join('\n');
      const innerDoc = markdownToTiptap(innerMd);
      content.push({
        type: 'blockquote',
        content: innerDoc.content ?? [{ type: 'paragraph' }],
      });
      continue;
    }

    // Task list
    const taskMatch = line.match(/^(\s*)- \[([ xX])\]\s+(.*)/);
    if (taskMatch) {
      const items: JSONContent[] = [];
      while (i < lines.length) {
        const tm = lines[i].match(/^(\s*)- \[([ xX])\]\s+(.*)/);
        if (!tm) break;
        items.push({
          type: 'taskItem',
          attrs: { checked: tm[2] !== ' ' },
          content: [{ type: 'paragraph', content: parseInline(tm[3]) }],
        });
        i++;
      }
      content.push({ type: 'taskList', content: items });
      continue;
    }

    // Unordered list
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.*)/);
    if (ulMatch && !line.match(/^(\s*)- \[/)) {
      const items: JSONContent[] = [];
      while (i < lines.length) {
        const um = lines[i].match(/^(\s*)[-*+]\s+(.*)/);
        if (!um || lines[i].match(/^(\s*)- \[/)) break;
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: parseInline(um[2]) }],
        });
        i++;
      }
      content.push({ type: 'bulletList', content: items });
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^(\s*)\d+\.\s+(.*)/);
    if (olMatch) {
      const items: JSONContent[] = [];
      while (i < lines.length) {
        const om = lines[i].match(/^(\s*)\d+\.\s+(.*)/);
        if (!om) break;
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: parseInline(om[2]) }],
        });
        i++;
      }
      content.push({ type: 'orderedList', content: items });
      continue;
    }

    // Image on its own line
    const imgMatch = line.match(/^!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)/);
    if (imgMatch) {
      content.push({
        type: 'image',
        attrs: {
          src: imgMatch[2],
          alt: imgMatch[1] || null,
          title: imgMatch[3] || null,
        },
      });
      i++;
      continue;
    }

    // Regular paragraph
    content.push({
      type: 'paragraph',
      content: parseInline(line),
    });
    i++;
  }

  return { type: 'doc', content };
}

function parseInline(text: string): JSONContent[] {
  if (!text) return [];

  const result: JSONContent[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
    if (boldMatch) {
      result.push({
        type: 'text',
        text: boldMatch[1],
        marks: [{ type: 'bold' }],
      });
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    // Italic
    const italicMatch = remaining.match(/^\*(.+?)\*/);
    if (italicMatch) {
      result.push({
        type: 'text',
        text: italicMatch[1],
        marks: [{ type: 'italic' }],
      });
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    // Strikethrough
    const strikeMatch = remaining.match(/^~~(.+?)~~/);
    if (strikeMatch) {
      result.push({
        type: 'text',
        text: strikeMatch[1],
        marks: [{ type: 'strike' }],
      });
      remaining = remaining.slice(strikeMatch[0].length);
      continue;
    }

    // Inline code
    const codeMatch = remaining.match(/^`(.+?)`/);
    if (codeMatch) {
      result.push({
        type: 'text',
        text: codeMatch[1],
        marks: [{ type: 'code' }],
      });
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    // Link
    const linkMatch = remaining.match(/^\[(.+?)\]\((.+?)\)/);
    if (linkMatch) {
      result.push({
        type: 'text',
        text: linkMatch[1],
        marks: [{ type: 'link', attrs: { href: linkMatch[2], target: '_blank' } }],
      });
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    // Regular text - consume until next special char
    const nextSpecial = remaining.search(/[*`~[]/);
    if (nextSpecial === -1) {
      result.push({ type: 'text', text: remaining });
      break;
    } else if (nextSpecial === 0) {
      // Single special char that didn't match a pattern - consume it
      result.push({ type: 'text', text: remaining[0] });
      remaining = remaining.slice(1);
    } else {
      result.push({ type: 'text', text: remaining.slice(0, nextSpecial) });
      remaining = remaining.slice(nextSpecial);
    }
  }

  return result.length > 0 ? result : [{ type: 'text', text: '' }];
}
